import { useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Phone, Mail } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Files } from "./Files";
import { WorkOrderForm, OrderRow } from "./WorkOrders";
import { nok, Field, Input, Area, Btn, Card, PageHead, Sheet, useData, ExportBtn, Spinner, RowsEditor } from "./ui";

const EMPTY = { name: "", phone: "", email: "", notes: "", vehicles: [] };

const CustomerForm = ({ initial, onClose, onSaved }) => {
  const [c, setC] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [detail, reloadDetail] = useData(c.id ? `/admin/customers/${c.id}` : "/admin/customers", c.id ? {} : { q: "__none__" });
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = c.id ? await api.put(`/admin/customers/${c.id}`, c) : await api.post("/admin/customers", c);
      setC({ ...EMPTY, ...data });
      toast.success("Kunde lagret");
      onSaved();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const orders = c.id && Array.isArray(detail?.work_orders) ? detail.work_orders : [];
  const totals = orders.reduce((s, o) => ({ total: s.total + o.calc.total, out: s.out + o.calc.outstanding }), { total: 0, out: 0 });
  return (
    <Sheet title={c.id ? c.name : "Ny kunde"} onClose={onClose} testid="customer-sheet">
      <div className="space-y-6">
        <Card className="grid gap-3 md:grid-cols-3">
          <Field label="Navn *"><Input value={c.name} onChange={set("name")} data-testid="customer-name-input" /></Field>
          <Field label="Telefon"><Input value={c.phone} onChange={set("phone")} data-testid="customer-phone-input" /></Field>
          <Field label="E-post"><Input type="email" value={c.email} onChange={set("email")} data-testid="customer-email-input" /></Field>
          <Field label="Interne notater" className="md:col-span-3"><Area value={c.notes} onChange={set("notes")} data-testid="customer-notes-input" /></Field>
        </Card>
        <Card>
          <h3 className="mb-3 font-display text-xl font-bold uppercase">Kjøretøy</h3>
          <RowsEditor rows={c.vehicles} setRows={(vehicles) => setC({ ...c, vehicles })} addLabel="Legg til kjøretøy" testid="customer-vehicles"
            blank={() => ({ reg_nr: "", vin: "", make: "", model: "", year: "" })}
            render={(v, patch, i) => (<>
              <Field label="Reg.nr" className="md:col-span-2"><Input value={v.reg_nr} onChange={(x) => patch({ reg_nr: x.target.value.toUpperCase() })} data-testid={`customer-vehicle-reg-${i}`} /></Field>
              <Field label="VIN" className="md:col-span-3"><Input value={v.vin} onChange={(x) => patch({ vin: x.target.value.toUpperCase() })} /></Field>
              <Field label="Merke" className="md:col-span-2"><Input value={v.make} onChange={(x) => patch({ make: x.target.value })} /></Field>
              <Field label="Modell" className="md:col-span-2"><Input value={v.model} onChange={(x) => patch({ model: x.target.value })} /></Field>
              <Field label="År" className="md:col-span-2"><Input value={v.year ?? ""} onChange={(x) => patch({ year: x.target.value })} /></Field>
            </>)} />
        </Card>
        <div className="sticky bottom-0 z-10 flex gap-2 border-t border-white/10 bg-[#08090b] py-4">
          <Btn variant="red" onClick={save} disabled={busy} data-testid="customer-save">Lagre</Btn>
          {c.id && <Btn onClick={() => setOrder({ customer_id: c.id, vehicle_id: c.vehicles[0]?.id ?? "" })} data-testid="customer-new-order"><Plus size={14} /> Ny arbeidsordre</Btn>}
          <Btn onClick={onClose}>Lukk</Btn>
        </div>
        {c.id && (
          <Card>
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
              <h3 className="font-display text-xl font-bold uppercase">Besøk og jobber ({orders.length})</h3>
              <span className="font-mono text-xs text-zinc-400" data-testid="customer-totals">Totalt {nok(totals.total)} · utestående {nok(totals.out)}</span>
            </div>
            <div className="space-y-2" data-testid="customer-orders">
              {orders.length === 0 && <p className="text-sm text-zinc-400">Ingen jobber ennå.</p>}
              {orders.map((o) => <OrderRow key={o.id} o={{ ...o, customer_name: c.name, vehicle: c.vehicles.find((v) => v.id === o.vehicle_id) }}
                onClick={() => setOrder({ ...o, parts: o.parts.map((p, i) => ({ id: `p${i}`, ...p })) })} />)}
            </div>
            {detail?.inquiries?.length > 0 && <p className="mt-3 text-xs text-zinc-400">{detail.inquiries.length} forespørsel(er) fra nettsiden er koblet til kunden.</p>}
          </Card>
        )}
        {c.id && <Card><h3 className="mb-3 font-display text-xl font-bold uppercase">Bilder og vedlegg</h3><Files ownerType="customer" ownerId={c.id} testid="customer-files" /></Card>}
      </div>
      {order && <WorkOrderForm initial={order} onClose={() => { setOrder(null); reloadDetail(); }} onSaved={reloadDetail} />}
    </Sheet>
  );
};

export const Customers = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [items, reload] = useData("/admin/customers", { q });
  return (
    <div data-testid="customers-page">
      <PageHead title="Kunderegister" sub={`${items?.length ?? 0} kunder`}>
        <ExportBtn kind="customers" testid="customers-export" />
        <Btn variant="red" onClick={() => setOpen({})} data-testid="customer-new"><Plus size={14} /> Ny kunde</Btn>
      </PageHead>
      <div className="mb-4 flex items-center gap-2 border border-white/15 px-3"><Search size={16} className="text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk navn, telefon, e-post, reg.nr, VIN" className="w-full bg-transparent py-2.5 text-sm outline-none" data-testid="customers-search" /></div>
      {!items ? <Spinner /> : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="customers-list">
          {items.length === 0 && <p className="border border-dashed border-white/15 p-10 text-center text-zinc-400 md:col-span-2">Ingen kunder.</p>}
          {items.map((c) => (
            <button key={c.id} type="button" onClick={() => setOpen(c)} data-testid={`customer-row-${c.id}`} className="border border-white/10 bg-[var(--surface)] p-4 text-left hover:border-white/30">
              <div className="font-display text-xl font-bold uppercase">{c.name}</div>
              <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-zinc-300">{c.phone && <span className="flex items-center gap-1"><Phone size={12} />{c.phone}</span>}{c.email && <span className="flex items-center gap-1"><Mail size={12} />{c.email}</span>}</div>
              <div className="mt-2 text-xs text-zinc-400">{c.vehicles.map((v) => `${v.reg_nr} ${v.make} ${v.model}`.trim()).join(" · ") || "Ingen kjøretøy"}</div>
              <div className="mt-2 font-mono text-[11px] text-zinc-400">{c.stats.visits} besøk · {nok(c.stats.total)}{c.stats.outstanding > 0 && <span className="text-amber-400"> · utestående {nok(c.stats.outstanding)}</span>}</div>
            </button>
          ))}
        </div>
      )}
      {open && <CustomerForm initial={open} onClose={() => { setOpen(null); reload(); }} onSaved={reload} />}
    </div>
  );
};
